import React, { useContext, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { AuthContext } from '../../context/AuthContext'
import { Input, Label, HelperText, Button } from '@windmill/react-ui'
import { Link } from 'react-router-dom'

function CreateAccountForm() {
    const { register }: any = useContext(AuthContext)
    const [statusReason, setStatusReason]: [any, any] = useState('')
    return (
        <Formik
            initialValues={{
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                passwordConfirmation: '',
                privacyOptIn: false,
            }}
            validationSchema={Yup.object().shape({
                first_name: Yup.string().required('First name is required'),
                last_name: Yup.string().required('Last name is required'),
                email: Yup.string().email().required('Email is required'),
                password: Yup.string()
                    .min(8, 'New password must be at least 8 characters')
                    .matches(
                        new RegExp('^.*[0-9].*$'),
                        'At least one number required'
                    )
                    .matches(
                        new RegExp('^.*[a-zA-Z].*$'),
                        'At least one letter required'
                    )
                    .required('Password is required'),
                passwordConfirmation: Yup.string()
                    .required('Confirm Password is required')
                    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
                privacyOptIn: Yup.boolean().oneOf(
                    [true],
                    'You must agree to the privacy policy'
                ),
            })}
            onSubmit={(
                {
                    first_name,
                    last_name,
                    email,
                    password,
                    passwordConfirmation,
                    privacyOptIn,
                },
                { setStatus, setSubmitting, setErrors }
            ) => {
                setSubmitting(true)
                setStatus()
                register(
                    first_name,
                    last_name,
                    email,
                    password,
                    passwordConfirmation,
                    privacyOptIn
                )
                    .then((statusReason: string) => {
                        setStatusReason(statusReason)
                        setSubmitting(false)
                    })
                    .catch((error: any) => {
                        const errors: any = {}
                        if (error.response) {
                            if (error.response.data.email) {
                                errors.email = error.response.data.email
                            }
                            if (error.response.data.password1) {
                                errors.password = error.response.data.password1
                            }
                            if (error.response.data.password2) {
                                errors.password = error.response.data.password2
                            }
                            if (error.response.data.domain) {
                                errors.domain = error.response.data.domain
                            }
                            setErrors(errors)
                        }
                        if (error.response.data.domain) {
                            setStatus(error.response.data.domain)
                        } else if (Object.keys(errors).length === 0) {
                            setStatus('An error occurred. Please try again.')
                        }
                        setSubmitting(false)
                    })
            }}
        >
            {({ errors, status, touched, isSubmitting }) => (
                <Form>
                    <Label className="mt-4">
                        <span>First Name</span>
                        <Field
                            className="mt-1"
                            as={Input}
                            name="first_name"
                            type="text"
                            placeholder="First Name"
                        />
                        <ErrorMessage name="first_name">
                            {(msg) => (
                                <HelperText valid={false}>{msg}</HelperText>
                            )}
                        </ErrorMessage>
                    </Label>
                    <Label className="mt-4">
                        <span>Last Name</span>
                        <Field
                            className="mt-1"
                            as={Input}
                            name="last_name"
                            type="text"
                            placeholder="Last Name"
                        />
                        <ErrorMessage name="last_name">
                            {(msg) => (
                                <HelperText valid={false}>{msg}</HelperText>
                            )}
                        </ErrorMessage>
                    </Label>

                    <Label className="mt-4">
                        <span>Email</span>
                        <Field
                            className="mt-1"
                            as={Input}
                            name="email"
                            type="email"
                            placeholder="john@doe.com"
                        />
                        <ErrorMessage name="email">
                            {(msg) => (
                                <HelperText valid={false}>{msg}</HelperText>
                            )}
                        </ErrorMessage>
                    </Label>

                    <Label className="mt-4">
                        <span>Password</span>
                        <Field
                            className="mt-1"
                            as={Input}
                            name="password"
                            type="password"
                            placeholder="***************"
                        />
                        <ErrorMessage name="password">
                            {(msg) => (
                                <HelperText valid={false}>{msg}</HelperText>
                            )}
                        </ErrorMessage>
                    </Label>

                    <Label className="mt-4">
                        <span>Confirm Password</span>
                        <Field
                            className="mt-1"
                            as={Input}
                            name="passwordConfirmation"
                            type="password"
                            placeholder="***************"
                        />
                        <ErrorMessage name="passwordConfirmation">
                            {(msg) => (
                                <HelperText valid={false}>{msg}</HelperText>
                            )}
                        </ErrorMessage>
                    </Label>

                    <Label className="mt-6 flex flex-col" check>
                        <div className="flex flex-none items-center">
                            <Field
                                as={Input}
                                name="privacyOptIn"
                                type="checkbox"
                            />
                            <div className="ml-2">
                                I agree to the{' '}
                                <Link
                                    className="underline"
                                    to="/auth/privacy-policy"
                                >
                                    privacy policy
                                </Link>
                            </div>
                        </div>
                        <ErrorMessage name="privacyOptIn">
                            {(msg) => (
                                <div className="flex flex-none items-center w-full">
                                    <HelperText valid={false}>{msg}</HelperText>
                                </div>
                            )}
                        </ErrorMessage>
                    </Label>

                    <Button
                        className="mt-4"
                        block
                        type="submit"
                        value="submit"
                        disabled={isSubmitting}
                    >
                        Create Account
                    </Button>
                    {status && <HelperText valid={false}>{status}</HelperText>}
                    {statusReason && (
                        <HelperText
                            className="text-lg font-semibold text-green-500 mt-3 block"
                            valid={true}
                        >
                            Please click on the confirmation link we sent you by
                            email to begin using our service.{' '}
                        </HelperText>
                    )}
                </Form>
            )}
        </Formik>
    )
}

export default CreateAccountForm
